import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import {
  BT_NEWSLETTERS,
  NEWSLETTER_TYPE,
} from "@components/Newsletter/types/Newsletter";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import NewsletterForm from "@features/newsletter/components/NewsletterForm";
import { cn } from "@util/helpers";

type ArticleInsideBTGNewsletterProps = {
  /** This is the classname of the component. */
  className?: string;
};

/**
 * Article Inside BTG Newsletter component in article page.
 * @figma https://www.figma.com/design/9O7z95a6ncgr3kFoOqJP7Q/BT-GLOBAL?node-id=18635-31777&m=dev
 */
const ArticleInsideBTGNewsletter = ({
  className,
}: ArticleInsideBTGNewsletterProps): React.ReactElement => {
  const sendDataToGTM = useGTMDispatch();
  const newsletter = BT_NEWSLETTERS.filter(
    (newsletter) => newsletter.type === NEWSLETTER_TYPE.BTGLOBAL
  );

  return (
    <div
      data-testid="article-inside-btg-newsletter-component"
      className={cn(
        "track-impression rounded-lg border border-gray-175 bg-white px-6 py-8 text-center shadow-[0px_4px_32px_0px_rgba(0,0,0,0.05)]",
        className
      )}
      data-section-name="newsletter"
      data-section-label="decoding asia 1"
    >
      <div className="mb-6 flex items-center justify-center gap-2 font-poppins text-xs font-medium leading-normal tracking-8% text-verticals-btblue">
        <div className="aspect-square h-[24px] w-[24px] overflow-hidden">
          <div className="animate-slideToTop">
            <IconFactory type={IconTypeEnum.envelope} />
            <IconFactory type={IconTypeEnum.globe} />
            <IconFactory type={IconTypeEnum.search} />
          </div>

          <div className="animate-slideToTop">
            <IconFactory type={IconTypeEnum.envelope} />
            <IconFactory type={IconTypeEnum.globe} />
            <IconFactory type={IconTypeEnum.search} />
          </div>
        </div>

        <span className="border-l border-verticals-btblue pl-2">
          DECODING ASIA
        </span>
      </div>

      <h3 className="mb-2 font-lct text-7xl font-normal leading-snug -tracking-2%">
        Navigate Asia in
        <br />a new global order
      </h3>

      <p className="font-lct text-lg font-normal leading-snug -tracking-2%">
        Get the insights delivered to your inbox.
      </p>

      <NewsletterForm
        newsletters={newsletter}
        className="mx-auto w-full lg:w-1/2 xl:w-5/12"
        formClassName={cn(
          "border rounded-full border-blue-100 py-2 pl-4 pr-2 transition-colors",
          "focus-within:border-verticals-btblue hover:border-verticals-btblue"
        )}
        inputClassName={cn(
          "font-poppins text-sm font-normal text-gray-850 transition-colors",
          "placeholder:text-blue-170"
        )}
        buttonClassName={cn(
          "p-2 aspect-square flex items-center justify-center bg-verticals-btblue border-none w-auto text-lg text-white hover:border-none"
        )}
        signUpButtonIcon={
          <IconFactory
            type={IconTypeEnum.rightArrow}
            className="text-white"
            size={24}
          />
        }
        isBordered={false}
        successMessage={
          <div
            data-testid="article-inside-btg-newsletter-success-message-component"
            className="flex items-center justify-center gap-2"
          >
            <span className="rounded-full bg-verticals-btblue p-1">
              <IconFactory
                type={IconTypeEnum.check}
                className="text-white"
                size={24}
              />
            </span>

            <span className="font-lucida text-base font-medium leading-normal -tracking-2% text-gray-850">
              {"You are now subscribed."}
            </span>
          </div>
        }
        onSuccessCallback={() => {
          sendDataToGTM({
            event: "custom_event",
            eventCategory: "newsletter",
            eventAction: "signup",
            eventLabel: "decoding asia 1",
          });
        }}
        onSubmitCallback={() => {
          sendDataToGTM({
            event: "custom_event",
            eventCategory: "newsletter",
            eventAction: "click",
            eventLabel: "decoding asia 1",
          });
        }}
      />
    </div>
  );
};

export default ArticleInsideBTGNewsletter;
